// Cross-tab single-flight refresh coordinator (Step C Part 3), dependency-injected so it is unit
// testable without real browser primitives.
//
// Exactly ONE tab rotates the single-use HttpOnly refresh cookie (electing itself via the Web Locks
// API with ifAvailable — it never queues behind the lock, which would rotate an already-consumed
// cookie); the others receive the resulting access token over a same-origin BroadcastChannel. The
// refresh cookie itself is HttpOnly and never crosses tabs; only the short-lived access token does.
//
// Lock timeout: a waiting tab waits for the electing tab's broadcast. If the electing tab CLOSES
// mid-refresh and never broadcasts, the waiter times out; the Web Lock is then free (the browser
// releases a dead context's locks), so the waiter RE-TRIES the lock rather than failing. It never
// rotates with a token it knows is stale — it sends whatever HttpOnly cookie the browser currently
// holds; if that cookie was already consumed by the dead tab, the server's reuse->revoke rejects it
// and the session ends honestly. Bounded attempts so a persistently-stuck peer cannot loop forever.
export function makeRefreshCoordinator({ locks, channel, rotate, lockName, chan,
                                         waitMs = 8000, maxAttempts = 3 }) {
  let localFlight = null;              // single-flight within THIS tab (also tames StrictMode double-mount)
  const waiters = new Set();
  const sinks = new Set();

  function makeWaiter() {
    let resolve, reject, timer, done = false;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    const w = {
      resolve: (v) => { if (!done) { done = true; clearTimeout(timer); waiters.delete(w); resolve(v); } },
      reject: (e) => { if (!done) { done = true; clearTimeout(timer); waiters.delete(w); reject(e); } },
    };
    waiters.add(w);
    timer = setTimeout(() => w.reject(new Error("cross-tab refresh timeout")), waitMs);
    return { promise, cancel: () => { if (!done) { done = true; clearTimeout(timer); waiters.delete(w); } } };
  }

  if (channel) {
    channel.onmessage = ({ data }) => {
      if (!data || data.ch !== chan) return;
      if (data.type === "access") {
        const tok = { access: data.access, sid: data.sid, account_id: data.account_id };
        for (const w of [...waiters]) w.resolve(tok);
        sinks.forEach((s) => s.onToken && s.onToken(tok));
      } else if (data.type === "ended") {
        for (const w of [...waiters]) w.reject(new Error("session ended"));
        sinks.forEach((s) => s.onEnd && s.onEnd());
      }
    };
  }

  async function refreshOnce() {
    if (localFlight) return localFlight;
    localFlight = (async () => {
      if (!locks || !channel) return await rotate();      // old browser: same-tab single-flight only
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const waiter = makeWaiter();
        let elected = false, result = null;
        try {
          await locks.request(lockName, { ifAvailable: true }, async (lock) => {
            if (!lock) return;                             // another tab holds it → wait for its token
            elected = true;
            result = await rotate();
            channel.postMessage({ type: "access", ch: chan, access: result.access,
                                  sid: result.sid, account_id: result.account_id });
          });
        } catch (e) {
          waiter.cancel();
          channel.postMessage({ type: "ended", ch: chan }); // our OWN rotate failed → end everywhere
          throw e;
        }
        if (elected) { waiter.cancel(); return result; }
        try {
          return await waiter.promise;                     // electing tab will broadcast the token
        } catch (_) {
          // timed out (electing tab likely died before broadcasting) → loop and try the lock again
        }
      }
      throw new Error("cross-tab refresh coordination failed");
    })().finally(() => { localFlight = null; });
    return localFlight;
  }

  return {
    refreshOnce,
    subscribe: (sink) => { sinks.add(sink); return () => sinks.delete(sink); },
    broadcastEnded: () => { if (channel) channel.postMessage({ type: "ended", ch: chan }); },
  };
}
